import * as d3 from 'd3'
import * as React from 'react'

export const useChartDimensions = (ref: React.RefObject<HTMLDivElement>) => {
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })

  React.useLayoutEffect(() => {
    if (!ref.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return
      // Round to whole pixels and bail out when nothing changed. Calling
      // setDimensions with a fresh object on every notification re-renders
      // even when the size is identical, and subpixel ResizeObserver churn
      // can then spiral into "Maximum update depth exceeded". Returning the
      // previous object lets React skip the render.
      const width = Math.round(entries[0].contentRect.width)
      const height = Math.round(entries[0].contentRect.height)
      setDimensions((previous) =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height },
      )
    })
    resizeObserver.observe(ref.current)
    return () => resizeObserver.disconnect()
  }, [ref])

  return dimensions
}

export const useFullscreen = (ref: React.RefObject<HTMLDivElement>) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [isSupported, setIsSupported] = React.useState(true)

  React.useEffect(() => {
    setIsSupported(
      document.fullscreenEnabled ||
        (document as any).webkitFullscreenEnabled ||
        (document as any).mozFullScreenEnabled ||
        (document as any).msFullscreenEnabled,
    )

    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === ref.current)
      setError(null)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [ref])

  const toggleFullscreen = React.useCallback(async () => {
    if (!ref.current) return

    if (!isSupported) {
      setError('Fullscreen is not supported in this browser')
      return
    }

    try {
      if (!document.fullscreenElement) {
        await ref.current.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
      setError(null)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to toggle fullscreen mode',
      )
    }
  }, [ref, isSupported])

  return { isFullscreen, toggleFullscreen, error, isSupported }
}

export const useChartInteraction = <T extends { dateObj?: Date }>(
  xScale: any,
  processedData: T[],
): {
  hoveredData: T | null
  handlePointerMove: (event: React.PointerEvent<Element>) => void
  handlePointerLeave: () => void
} => {
  const [hoveredData, setHoveredData] = React.useState<T | null>(null)
  const lastIndexRef = React.useRef<number | null>(null)
  const xScaleRef = React.useRef(xScale)
  const processedDataRef = React.useRef(processedData)

  xScaleRef.current = xScale
  processedDataRef.current = processedData

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<Element>) => {
      const currentXScale = xScaleRef.current
      const currentData = processedDataRef.current
      if (!currentXScale || !currentData?.length) return

      const [xm] = d3.pointer(event)
      const date = currentXScale.invert(xm)
      const i = d3.bisector((d: T) => d.dateObj).center(currentData, date)

      if (lastIndexRef.current === i) return
      lastIndexRef.current = i

      const dataPoint = currentData[i]
      if (dataPoint) {
        setHoveredData({
          ...dataPoint,
          index: i,
        } as T)
      }
    },
    [],
  )

  const handlePointerLeave = React.useCallback(() => {
    lastIndexRef.current = null
    setHoveredData(null)
  }, [])

  return { hoveredData, handlePointerMove, handlePointerLeave }
}

export const useChartBase = (
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void,
) => {
  const containerRef = React.useRef<HTMLDivElement>(
    null,
  ) as React.RefObject<HTMLDivElement>
  const dimensions = useChartDimensions(containerRef)
  const { isFullscreen, toggleFullscreen, error, isSupported } =
    useFullscreen(containerRef)
  const [hoveredData, setHoveredData] = React.useState<any>(null)
  const onChartReadyRef = React.useRef(onChartReady)
  const calledRef = React.useRef(false)

  onChartReadyRef.current = onChartReady

  React.useEffect(() => {
    if (!calledRef.current && onChartReadyRef.current && containerRef.current) {
      calledRef.current = true
      onChartReadyRef.current({ containerRef, isFullscreen, toggleFullscreen })
    }
  }, [isFullscreen, toggleFullscreen])

  return {
    containerRef,
    dimensions,
    isFullscreen,
    toggleFullscreen,
    fullscreenError: error,
    fullscreenSupported: isSupported,
    hoveredData,
    setHoveredData,
  }
}
